import React, {useLayoutEffect, useRef, useState} from 'react'

import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import {useCalcContext} from "../../contexts/CalcContext";
import CircularProgress from '@mui/material/CircularProgress';
import Divider from "@mui/material/Divider";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import classnames from "classnames";
import {useThemeStore} from "../../stores/themeStore";


const nextYear = new Date().getFullYear() + 1
const allYears = Array.from({ length: nextYear - 2015 + 1 }, (_, i) => String(2015 + i)).reverse();
const newYears = allYears.slice(0, 3)

// Loading stand-in, centred on the chevron's spot. Module-level so the icon
// doesn't remount (and skip its open turn) on every render.
const SelectSpinner = (props) => (
    <CircularProgress {...props} size={13} style={{marginRight: '-1.5px', marginTop: '-3.5px', color: 'var(--text-secondary)'}}/>
)

export const CalcSelectCar = () => {
    const {
        carState,
        setCarState,
        getMakesForYear,
        getMakesForYearResponse,
        getModelsForMake,
        getModelsForMakeResponse,
        getTrimsForModelResponse,
        getTrimsForModel,
        getCarByVinResponse,
        calcState,
        setCalcState,
        isUsed,
        setIsUsed,
    } = useCalcContext()

    const isDark = useThemeStore((state) => state.theme) === 'dark'

    const [isTrimSelectOpen, setIsTrimSelectOpen] = useState(false)
    const [isMakeSelectOpen, setIsMakeSelectOpen] = useState(false)
    const [isModelSelectOpen, setIsModelSelectOpen] = useState(false)
    // After picking a value, queue the next select to auto-open once its
    // options finish loading (they arrive asynchronously).
    const [pendingOpen, setPendingOpen] = useState(null)   // 'make' | 'model' | 'trim'

    // Uppercasing the VIN rewrites the input; put the caret back where it was.
    const caretRef = useRef(null)
    useLayoutEffect(() => {
        const held = caretRef.current
        if (!held) return
        caretRef.current = null
        held.input.setSelectionRange(held.start, held.end)
    })

    // Drop the menu directly below the select, left-aligned with it — otherwise
    // MUI's default "selectedMenu" positioning shifts wide menus far to the left.
    // PaperProps.className gives the portaled Paper a CSS hook for dark/light
    // theming (the Paper renders in <body>, outside #CalculatePage's token scope).
    const selectMenuProps = {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        marginThreshold: 4,
        PaperProps: { className: 'calc-select-dropdown-paper' },
    }

    // Undefined falls back to the theme's chevron.
    const iconFor = (isLoading) => (getCarByVinResponse?.loading || isLoading) ? SelectSpinner : undefined

    useDidMountEffect(function openTrimSelectIfMultipleCars(){
        if (carState.vin_vehicles.length > 1) {
            setIsTrimSelectOpen(true)
        }
    }, [carState.vin_vehicles])

    useDidMountEffect(function autoOpenMake(){
        if (pendingOpen === 'make' && carState.makes.length) {
            setIsMakeSelectOpen(true)
            setPendingOpen(null)
        }
    }, [carState.makes])

    useDidMountEffect(function autoOpenModel(){
        if (pendingOpen === 'model' && carState.models.length) {
            setIsModelSelectOpen(true)
            setPendingOpen(null)
        }
    }, [carState.models])

    useDidMountEffect(function autoOpenTrim(){
        if (pendingOpen === 'trim' && carState.trims.length) {
            setIsTrimSelectOpen(true)
            setPendingOpen(null)
        }
    }, [carState.trims])

    const isTrimInVinVehicles = (trimId) => {
        const vinVechileIds = carState.vin_vehicles.map(v => String(v.id))
        return vinVechileIds.includes(String(trimId))
    }

    const isRebatesDisabled = !calcState.base_car_id || !calcState.location_id || !calcState.dealer_location_id

    const { makes, models, trims, options } = carState

    const years = isUsed ? allYears : newYears

    return (
        <Box className={'CalcPageInfo_box'}>
            <Stack
                direction={'column'}
                justifyContent="space-between"
                style={{
                    height: '100%'
                }}
            >
                <div>
                    <Stack
                        style={{ width: '100%' }}
                        direction={'row'}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="h6"
                            component="div"
                            className={'header location-info'}
                        >
                            <DirectionsCarFilledOutlinedIcon color="primary"/>
                            <span>
                            Select Vehicle
                        </span>
                        </Typography>
                        <div className={'header location-info tab-toggle'}>
                            <button
                                type="button"
                                className={classnames('tab-btn', { active: !isUsed })}
                                onClick={() => setIsUsed(false)}
                            >
                                New
                            </button>
                            <span className="sep">/</span>
                            <button
                                type="button"
                                className={classnames('tab-btn', { active: isUsed })}
                                onClick={() => setIsUsed(true)}
                            >
                                Used
                            </button>
                        </div>

                    </Stack>
                    <div className={'header-title'}>
                        {/* Year / Make / Model / Trim — placeholder text appears
                            INSIDE the select itself via displayEmpty + renderValue,
                            so there's no separate <label> on the left. */}
                        <div className={'select-row'}>
                            <Select
                                className={'Select'}
                                displayEmpty
                                labelId="pricing-select-year"
                                id="year-select"
                                value={carState.year}
                                MenuProps={selectMenuProps}
                                IconComponent={iconFor(false)}
                                renderValue={(val) => val
                                    ? val
                                    : <span className={'select-placeholder'}>Year</span>}
                                onChange={(e) => {
                                    const year = e.target.value
                                    setCarState({
                                        ...carState,
                                        year: year,
                                        make: '',
                                        model: '',
                                        trim: '',
                                        options: []
                                    })

                                    setCalcState({...calcState, vin: ''})

                                    getMakesForYear(isUsed)
                                    setPendingOpen('make')
                                }}
                            >
                                {
                                    // Most recent year first (descending) — allYears is already reversed at construction.
                                    years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)
                                }
                            </Select>
                        </div>
                        <div className={'select-row'}>
                            <Select
                                className={'Select'}
                                displayEmpty
                                IconComponent={iconFor(getMakesForYearResponse?.loading)}
                                value={carState.make}
                                disabled={!carState.year || ! carState.makes.length}
                                MenuProps={selectMenuProps}
                                open={isMakeSelectOpen}
                                onOpen={() => setIsMakeSelectOpen(true)}
                                onClose={() => setIsMakeSelectOpen(false)}
                                renderValue={(val) => val
                                    ? val
                                    : <span className={'select-placeholder'}>Make</span>}
                                onChange={(e) => {
                                    const make = e.target.value
                                    setCarState({
                                        ...carState,
                                        make: make,
                                        model: '',
                                        trim: '',
                                        options: []
                                    })
                                    setCalcState({...calcState, vin: ''})

                                    getModelsForMake(carState.year, make, isUsed)
                                    setPendingOpen('model')
                                }}
                            >
                                {
                                    makes.map((make) => <MenuItem key={make.cleaned_name} value={make.cleaned_name}>{make.cleaned_name}</MenuItem>)
                                }
                            </Select>
                        </div>
                        <div className={'select-row'}>
                            <Select
                                className={'Select'}
                                displayEmpty
                                IconComponent={iconFor(getModelsForMakeResponse?.loading)}
                                value={carState.model}
                                disabled={!carState.make || ! carState.models.length}
                                MenuProps={selectMenuProps}
                                open={isModelSelectOpen}
                                onOpen={() => setIsModelSelectOpen(true)}
                                onClose={() => setIsModelSelectOpen(false)}
                                renderValue={(val) => val
                                    ? val
                                    : <span className={'select-placeholder'}>Model</span>}
                                onChange={(e) => {
                                    const model = e.target.value
                                    setCarState({
                                        ...carState,
                                        model: model,
                                        trim: '',
                                        options: []
                                    })

                                    setCalcState({...calcState, vin: ''})
                                    getTrimsForModel(carState.year, carState.make, model, isUsed)
                                    setPendingOpen('trim')
                                }}
                            >
                                {
                                    models?.map((model) => <MenuItem key={model.name} value={model.cleaned_name}>{model.cleaned_name}</MenuItem>)
                                }
                            </Select>
                        </div>
                        <div className={'select-row'}>
                            <Select
                                className={'Select'}
                                displayEmpty
                                value={carState.trim}
                                MenuProps={selectMenuProps}
                                onClose={() => setIsTrimSelectOpen(false)}
                                onOpen={() => setIsTrimSelectOpen(true)}
                                open={isTrimSelectOpen}
                                disabled={!carState.model|| ! carState.trims.length}
                                IconComponent={iconFor(getTrimsForModelResponse?.loading)}
                                renderValue={(val) => {
                                    if (!val) return <span className={'select-placeholder'}>Trim</span>
                                    try {
                                        const t = JSON.parse(val)
                                        return t.name || ''
                                    } catch (_e) { return val }
                                }}
                                onChange={(e) => {
                                    const trim = JSON.parse(e.target.value)
                                    const last_deal_data = trim.last_deal_data

                                    setCarState({
                                        ...carState,
                                        trim: JSON.stringify(trim),
                                        options: trim.option_codes,
                                        car_id: trim.id,
                                        base_car_id: trim.id,
                                    })

                                    // A VIN that decodes to more than one candidate leaves the
                                    // trim unresolved, so the user has to finish the decode from
                                    // this dropdown. That first selection is part of the decode —
                                    // it settles *which* car this is, not what it costs — so the
                                    // trim's last-deal prefill must not overwrite the MSRP the
                                    // listing supplied. Every later trim change is a deliberate
                                    // edit and prefills as usual.
                                    //
                                    // carState.trim is empty only while the decode is unresolved
                                    // (this handler fills it), and Year/Make/Model each clear
                                    // calcState.vin, so an empty vin means "not decoded" and the
                                    // manual flow is untouched. An empty msrp means there is
                                    // nothing to protect.
                                    const isResolvingVinDecode = carState.trim === ''
                                        && Boolean(calcState.vin)
                                        && calcState.msrp !== '' && calcState.msrp != null

                                    setCalcState({
                                        ...calcState,
                                        car_id: trim.id,
                                        base_car_id: trim.id,
                                        ...(isResolvingVinDecode ? {} : {msrp: last_deal_data?.msrp}),
                                        cost: last_deal_data?.cost,
                                        // profit: last_deal_data?.profit,
                                        // vin: '',
                                    })
                                    setTimeout(() => {
                                        setIsTrimSelectOpen(false)
                                    }, 0)

                                }}
                            >
                                {
                                    trims?.map((trim) =>
                                        <MenuItem
                                            key={trim.id}
                                            value={JSON.stringify(trim)}
                                            sx={{
                                                backgroundColor: isTrimInVinVehicles(trim.id)
                                                    ? (isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)")
                                                    : "inherit"
                                            }}
                                        >
                                            {trim.name}<span style={{fontSize: '11px', marginLeft: '5px'}}> [{trim.manufacture_code}]</span>
                                        </MenuItem>)
                                }
                            </Select>
                        </div>
                        {
                            Boolean(options?.length) &&
                            <div className={'select-row'}>
                                <Tooltip
                                    title={'Option codes are not necessary but can affect rebates.'}
                                    arrow placement={'right'}
                                >
                                    <Select
                                        className={'Select'}
                                        displayEmpty
                                        multiple={true}
                                        MenuProps={selectMenuProps}
                                        IconComponent={iconFor(getModelsForMakeResponse?.loading)}
                                        value={carState.option_codes}
                                        disabled={!carState.make || !carState.models.length || !carState.trims.length}
                                        renderValue={(val) => (!val || val.length === 0)
                                            ? <span className={'select-placeholder'}>Options</span>
                                            : val.map((v) => v?.name).filter(Boolean).join(', ')}
                                        onChange={(e) => {
                                            const option_codes = e.target.value
                                            setCarState({
                                                ...carState,
                                                option_codes: option_codes
                                            })
                                            const option_code_ids = option_codes.map(c => c.id)

                                            setCalcState({...calcState, option_code_ids: option_code_ids})
                                        }}
                                    >
                                        {
                                            options?.map((option) => <MenuItem key={option.id} value={option}>{option.name}</MenuItem>)
                                        }
                                    </Select>
                                </Tooltip>
                            </div>

                        }

                        <Divider>Or</Divider>
                        {/* VIN — no label; placeholder reads "Enter a Vin" */}
                        <div className={'select-row'}>
                            <Input
                                className={'vin-input'}
                                type="text"
                                value={calcState.vin}
                                placeholder={'Enter a Vin'}
                                onChange={(e) => {
                                    const {value, selectionStart, selectionEnd} = e.target
                                    caretRef.current = {input: e.target, start: selectionStart, end: selectionEnd}
                                    setCalcState({...calcState, vin: value.toUpperCase()})
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Stack>
        </Box>
    )
}